// "Share with family" (Carla's decision 4, 2026-10-12). Personal notes only: the Notes page shows
// this only in a Personal workspace, and the server refuses any other. Choose people in this
// family; they read the note in their own Notes under "Shared with me", in their Personal view
// only, and cannot change it. Saving sends exactly the chosen list ([] unshares it), and what is
// shown afterwards is what the server sent back, never what was assumed.
// Carla's item 4b (2026-10-12): a share covers the pages inside the page, now and later, and the
// dialog says so ("This page and the N pages inside it will be shared with ..."), unsharing too.
// On a page inside a shared page, a member who already reads it through that page is shown ticked
// and fixed (it is changed on that page) and is never sent as this page's own share.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { messageFromError } from "../../utils/apiError";
import { describeWriteFailure } from "../../utils/writeFailure";
import { showToast } from "../../utils/toast";
import { Modal, Button, Checkbox, Skeleton, Banner } from "../../ui";

const listNames = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);
const pagesPhrase = (inside) => (inside > 0 ? `This page and the ${inside} ${inside === 1 ? 'page' : 'pages'} inside it` : 'This page');

// What Save will do, in one sentence; null when nothing would change.
export function shareSentence({ inside, members, chosen }) {
  const own = members.filter(m => !m.sharedThrough);
  const now = own.filter(m => chosen.has(m.id)).map(m => m.name);
  const dropped = own.filter(m => m.shared && !chosen.has(m.id)).map(m => m.name);
  const added = own.filter(m => !m.shared && chosen.has(m.id));
  if (!dropped.length && !added.length) return null;
  if (!now.length) return `${pagesPhrase(inside)} will no longer be shared with ${listNames(dropped)}.`;
  return `${pagesPhrase(inside)} will be shared with ${listNames(now)}${dropped.length ? `, and no longer with ${listNames(dropped)}` : ''}.`;
}

export default function ShareWithFamily({ open, onClose, note, ensureSaved }) {
  const [load, setLoad] = useState({ status: 'idle', members: [], inside: 0, error: null });
  const [chosen, setChosen] = useState(() => new Set());
  const [save, setSave] = useState({ busy: false, error: null });
  const noteId = note?.id;

  const fetchMembers = useCallback(async () => {
    setLoad({ status: 'loading', members: [], inside: 0, error: null });
    try {
      if (ensureSaved && !(await ensureSaved())) throw new Error('This note has not been saved yet. Save it, then share it.');
      const data = await api(`/api/notes/${noteId}/shares`).then(r => r.json());
      if (!Array.isArray(data?.members)) throw new Error('Your family did not load: Aidey sent back something it could not read.');
      setLoad({ status: 'ready', members: data.members, inside: Number(data.inside) || 0, error: null });
      setChosen(new Set(data.members.filter(m => m.shared && !m.sharedThrough).map(m => m.id)));
    } catch (err) {
      setLoad({ status: 'failed', members: [], inside: 0, error: messageFromError(err, 'Your family did not load.') });
    }
  }, [noteId, ensureSaved]);

  useEffect(() => {
    if (open) { setSave({ busy: false, error: null }); fetchMembers(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noteId]);

  async function submit() {
    setSave({ busy: true, error: null });
    try {
      const memberIds = load.members.filter(m => chosen.has(m.id) && !m.sharedThrough).map(m => m.id);
      const data = await api(`/api/notes/${noteId}/shares`, { method: 'PUT', body: JSON.stringify({ memberIds }) }).then(r => r.json());
      if (!Array.isArray(data?.members)) throw new Error('Aidey did not say who it is shared with now. Open Share with family again to check.');
      // Said from what the server sent back: who has it now (its own share or through a page above).
      const now = data.members.filter(m => m.shared || m.sharedThrough).map(m => m.name);
      const inside = Number(data.inside) || 0;
      const verb = inside > 0 ? 'are' : 'is';
      setSave({ busy: false, error: null });
      onClose();
      showToast(now.length ? `${pagesPhrase(inside)} ${verb} shared with ${listNames(now)}.` : `${pagesPhrase(inside)} ${verb} not shared with anyone now.`, { type: 'success' });
    } catch (err) {
      setSave({ busy: false, error: err?.status ? describeWriteFailure(err, 'share that note') : messageFromError(err, 'That was not shared.') });
    }
  }

  const sentence = load.status === 'ready' ? shareSentence({ inside: load.inside, members: load.members, chosen }) : null;

  const footer = (
    <div className="nt-share__foot">
      {save.error && <p className="nt-failed" role="alert">{save.error}</p>}
      <div className="nt-share__buttons">
        <Button variant="tertiary" onClick={onClose} disabled={save.busy}>Cancel</Button>
        <Button variant="confirm" onClick={submit} disabled={save.busy || load.status !== 'ready' || !load.members.length}>
          {save.busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={save.busy ? undefined : onClose} title="Share with family" color="group-my-work" width={480} footer={footer}>
      <div className="nt-share">
        <p className="nt-share__lead">
          The people you choose can read this note in their own Notes, under Shared with me. They cannot change it,
          and it only shows in their Personal view, never at work.
        </p>
        {load.status === 'loading' || load.status === 'idle' ? (
          <Skeleton lines={3} label="Loading your family" />
        ) : load.status === 'failed' ? (
          <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={fetchMembers}>Try again</Button>}>{load.error}</Banner>
        ) : !load.members.length ? (
          <p className="nt-panel__empty">No one else is in your family yet. Add family members in Family, then share notes with them here.</p>
        ) : (
          <div className="nt-share__list" role="group" aria-label="Family members">
            {load.members.map(m => (m.sharedThrough ? (
              <Checkbox key={m.id} checked disabled onChange={() => {}}
                label={`${m.name} (shared through ${m.sharedThrough.title}, the page this is in: change it there)`} />
            ) : (
              <Checkbox key={m.id} checked={chosen.has(m.id)}
                onChange={(on) => setChosen(prev => { const next = new Set(prev); if (on) next.add(m.id); else next.delete(m.id); return next; })}
                label={m.hasLogin ? m.name : `${m.name} (no login yet: they will see it once they have one)`} />
            )))}
          </div>
        )}
        {load.status === 'ready' && load.members.length > 0 && (sentence || load.inside > 0) && (
          <p className="nt-share__effect" data-testid="nt-share-effect" aria-live="polite">
            {sentence || `Sharing this page shares the ${load.inside === 1 ? 'page' : `${load.inside} pages`} inside it too.`}
          </p>
        )}
      </div>
    </Modal>
  );
}
